import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Card, message, Spin, Tabs, Typography } from 'antd'
import { menuAdminRequest, errorMessage } from './api'
import { parsePolicy, relationOnly } from './model'
import ChangeConfirm from './ChangeConfirm'
import PolicyPanel from './PolicyPanel'
import RelationPanel from './RelationPanel'
import HistoryPanel from './HistoryPanel'
import ObservationPanel from './ObservationPanel'
import HelpHint from './HelpHint'
import './index.less'

// 2026-10-20 coder(lq): 独立权限控制台；只读初始化失败时不展示可写表单，不把错误伪装成空数据。
export default function MenuApiPermission() {
    const [settings, setSettings] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [tab, setTab] = useState('relations')
    const [change, setChange] = useState(null)
    const [busy, setBusy] = useState(false)
    const [revision, setRevision] = useState(0)
    const reportError = useCallback(value => setError(value), [])
    const load = useCallback(async () => {
        setLoading(true); setError('')
        try { const result = await menuAdminRequest('bootstrap'); setSettings(result); return true }
        catch (err) { setSettings(null); setError(errorMessage(err)); return false }
        finally { setLoading(false) }
    }, [])
    useEffect(() => { load() }, [load])
    const reviewRelation = (action, before, after) => setChange({
        path: 'relations/change', before: relationOnly(before), after: relationOnly(after),
        body: { action, before: relationOnly(before), after: relationOnly(after) },
        warning: action === 'DELETE' ? '删除最后一条关系后，接口将变成“未映射”，当前不会被新增菜单校验拦截。请确认不是在扩大访问范围。'
            : action === 'EDIT' ? '修改路径或方法会移除原关系；若原接口因此变成未映射，当前不会被新增菜单校验拦截，请同时核对旧接口。' : undefined,
    })
    const reviewPolicy = (policy, summary) => {
        try {
            setChange({ path: 'policy', before: parsePolicy(settings.policyJson), after: policy,
                body: { policyJson: JSON.stringify(policy), version: settings.version }, summary,
                warning: !policy.enabled || policy.gatewayMode === 'OFF'
                    ? '保存后将停用新增菜单接口校验，不改变原有登录保护。'
                    : policy.gatewayMode === 'AUDIT' ? '保存后启用观察，不新增权限阻断；请核对各系统及豁免范围。'
                        : '保存后启用网关正式判断；只有系统也设为正式拦截的已映射接口才会阻断。共享接口可能受影响，请先完成观察验证。' })
        } catch (err) { reportError(err.message) }
    }
    const save = async reason => {
        setBusy(true); setError('')
        try {
            await menuAdminRequest(change.path, { method: 'POST', csrfToken: settings.csrfToken,
                data: { ...change.body, reason, confirmed: true } })
            setChange(null); message.success('已保存，请核对最新关系及策略状态')
            setRevision(value => value + 1); await load()
        } catch (err) {
            reportError(errorMessage(err))
            // 2026-10-20 coder(lq): 冲突/超时均不自动重试写入；先刷新核对提交状态，避免重复变更。
            setChange(null); message.error('未确认保存成功，请刷新并核对记录后再操作')
        } finally { setBusy(false) }
    }
    return <main className="menu-api-page">
        <header className="menu-api-heading"><Typography.Title level={2}>菜单接口权限
            <HelpHint label="菜单接口权限说明">维护菜单与接口关系，逐系统观察和启用，保留每次手工变更证据。
                {settings && !loading && (settings.enabled
                    ? '页面总开关已开启；请在“系统权限开关”核对网关模式和各系统模式。'
                    : '当前总开关关闭：可在“系统权限开关”统一配置并核对保存，无需配置 Apollo。')}
            </HelpHint></Typography.Title>
            <Button loading={loading} disabled={busy} onClick={load}>刷新状态</Button></header>
        {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} />}
        {/* 2026-10-20 coder(lq): 刷新已有配置时保留页签组件，保存后不丢失当前菜单、筛选与树展开状态。 */}
        {loading && !settings ? <div className="menu-api-loading" role="status"><Spin /><span>正在读取权限配置…</span></div> : settings ?
            <Spin spinning={loading || busy}><Card><Tabs activeKey={tab} onChange={setTab} items={[
                { key: 'relations', label: '菜单接口关系', children: tab === 'relations' && <RelationPanel settings={settings} revision={revision} onReview={reviewRelation} onError={reportError} /> },
                { key: 'policy', label: '系统权限开关', children: tab === 'policy' && <PolicyPanel settings={settings} onReview={reviewPolicy} onError={reportError} /> },
                { key: 'observations', label: '观察记录', children: tab === 'observations' && <ObservationPanel /> },
                { key: 'history', label: '手工变更记录', children: tab === 'history' && <HistoryPanel revision={revision} onError={reportError} /> },
            ]} /></Card></Spin> : <Card><Typography.Paragraph>未取得管理权限或服务尚未就绪。请先登录，并由管理员授予独立管理菜单；不要在浏览器填写内部凭据。</Typography.Paragraph></Card>}
        <ChangeConfirm change={change} busy={busy} onCancel={() => setChange(null)} onSubmit={save} />
    </main>
}
